import Vue from 'vue'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
      continue; // Don't get the children since it will reload the template
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        Component = await Component()
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: '/',
      env: {"ENV_THEME":"staging","BASE_TITLE":"Staging - IQRecruit","FIRE_ENV":"staging","STRIPE_PUBLISHABLE_KEY":"pk_test_ZxyA19PSh2S5RqUBGKfrpgJe00son0mgaO","API_URL":"https://tech.staging.iqtalentx.com","FF_USE_WINDOWS_LEGACY_PROCESS_STRATEGY":"true","FF_SCRIPT_SECTIONS":"false","FF_POSIXLY_CORRECT_ESCAPES":"false","CI_BUILD_ID":"3549352219","CI_SERVER_VERSION_PATCH":"0","CI_SERVER_REVISION":"6cf5dc1c7d3","CI_COMMIT_SHORT_SHA":"af700f13","GITLAB_USER_LOGIN":"loganserman","npm_package_devDependencies_firebase_tools":"^8.0.0","npm_package_devDependencies__mdi_font":"^5.0.45","npm_package_dependencies_papaparse":"^5.2.0","CI_DEPENDENCY_PROXY_PASSWORD":"64_zQ4Zjy4zgyW7oFDEsiTN","CI_DEPENDENCY_PROXY_SERVER":"gitlab.com:443","npm_package_devDependencies__nuxtjs_eslint_module":"^1.0.0","npm_package_scripts_test_cypress":"env API_URL=http://localhost:5017 yarn build && yarn start","CI_BUILD_REF":"af700f13ee0f8a1f9e05b1b87243e16e692e27ea","npm_package_devDependencies_jest":"^25.2.4","npm_package_devDependencies__nuxtjs_dotenv":"^1.4.1","npm_config_version_commit_hooks":"true","npm_config_user_agent":"yarn/1.22.5 npm/? node/v10.24.1 linux x64","FF_USE_LEGACY_KUBERNETES_EXECUTION_STRATEGY":"false","CI":"true","CI_SERVER_PROTOCOL":"https","CI_PROJECT_NAME":"iqtx","SENTRY_AUTH_TOKEN":"0b467d54797243d09178ea2eca929d8c4c501a0bab6f4a9eb16bfb2af20ef745","CI_RUNNER_REVISION":"a889181a","NODE_VERSION":"10.24.1","npm_config_bin_links":"true","HOSTNAME":"runner-zxwgkjap-project-16199796-concurrent-0","CI_JOB_STAGE":"deploy","CI_PROJECT_DESCRIPTION":"","CI_COMMIT_DESCRIPTION":"","YARN_VERSION":"1.22.5","npm_node_execpath":"/usr/local/bin/node","npm_package_devDependencies_vue_jest":"^4.0.0-0","npm_config_init_version":"1.0.0","CI_DEPENDENCY_PROXY_USER":"gitlab-ci-token","CI_SERVER_VERSION":"15.8.0-pre","SHLVL":"6","CI_PROJECT_ROOT_NAMESPACE":"iq-talent-partners","HOME":"/root","OLDPWD":"/","npm_package_devDependencies_eslint_plugin_nuxt":">=0.4.2","npm_package_dependencies_apollo_link_error":"^1.1.13","FF_NETWORK_PER_BUILD":"false","CI_JOB_ID":"3549352219","CI_SERVER_HOST":"gitlab.com","CI_COMMIT_REF_NAME":"develop","npm_package_devDependencies_faker":"^5.2.0","npm_package_devDependencies_eslint_config_prettier":"^6.9.0","CI_PIPELINE_SOURCE":"push","CI_RUNNER_VERSION":"15.6.0~beta.186.ga889181a","npm_package_devDependencies_eslint_plugin_cypress":"^2.11.1","npm_config_init_license":"MIT","FF_SKIP_NOOP_BUILD_STAGES":"true","FF_USE_FASTZIP":"false","CI_BUILDS_DIR":"/builds","CI_ENVIRONMENT_NAME":"staging","CI_SERVER_VERSION_MAJOR":"15","CI_DEFAULT_BRANCH":"develop","SENTRY_PUBLISH_RELEASE":"true","YARN_WRAP_OUTPUT":"false","npm_package_devDependencies_eslint_loader":"^3.0.3","npm_package_devDependencies__nuxtjs_vuetify":"^1.11.0","npm_package_dependencies__nuxtjs_sentry":"^4.5.0","npm_package_dependencies__nuxtjs_firebase":"^6.1.0","npm_config_version_tag_prefix":"v","CI_BUILD_TOKEN":"64_zQ4Zjy4zgyW7oFDEsiTN","CI_REGISTRY_PASSWORD":"64_zQ4Zjy4zgyW7oFDEsiTN","CI_SERVER_URL":"https://gitlab.com","CI_TEMPLATE_REGISTRY_HOST":"registry.gitlab.com","CI_COMMIT_REF_PROTECTED":"true","CI_ENVIRONMENT_ACTION":"start","npm_package_dependencies_graphql_tag":"^2.10.3","GITLAB_FEATURES":"elastic_search,ldap_group_sync,multiple_ldap_servers,seat_link,usage_quotas,repository_size_limit,admin_audit_log,auditor_user,custom_file_templates,custom_project_templates,db_load_balancing,default_branch_protection_restriction_in_groups,extended_audit_events,external_authorization_service_api_management,geo,ldap_group_sync_filter,object_storage,pages_size_limit,password_complexity,project_aliases,enterprise_templates,git_abuse_rate_limit,required_ci_templates,runner_maintenance_note,runner_upgrade_management,runner_jobs_statistics","CI_PROJECT_ID":"16199796","CI_REGISTRY_IMAGE":"registry.gitlab.com/iq-talent-partners/iqtx","npm_package_devDependencies_husky":"^4.2.3","GITLAB_CI":"true","CI_COMMIT_SHA":"af700f13ee0f8a1f9e05b1b87243e16e692e27ea","npm_package_dependencies_apollo_cache_inmemory":"^1.6.5","npm_package_description":"the itq ppl exchange","CI_CONCURRENT_ID":"204","npm_package_readmeFilename":"README.md","npm_package_dependencies_nuxt":"^2.13.3","FF_DISABLE_UMASK_FOR_DOCKER_EXECUTOR":"false","CI_REGISTRY_USER":"gitlab-ci-token","CI_SERVER_PORT":"443","npm_package_devDependencies_prettier":"^2.0.2","npm_package_devDependencies_babel_jest":"^25.2.4","npm_package_scripts_dev":"nuxt","CI_PROJECT_DIR":"/builds/iq-talent-partners/iqtx","CI_PROJECT_PATH":"iq-talent-partners/iqtx","npm_package_dependencies_vue2_editor":"^2.10.2","FF_ENABLE_JOB_CLEANUP":"false","DOCKER_DRIVER":"overlay2","CI_PROJECT_NAMESPACE":"iq-talent-partners","CI_COMMIT_TIMESTAMP":"2023-01-03T20:06:22+00:00","npm_package_devDependencies__mdi_js":"^5.3.45","npm_package_private":"true","FF_USE_DIRECT_DOWNLOAD":"true","FF_USE_DYNAMIC_TRACE_FORCE_SEND_INTERVAL":"false","CI_JOB_TOKEN":"64_zQ4Zjy4zgyW7oFDEsiTN","CI_NODE_TOTAL":"1","CI_SERVER_NAME":"GitLab","npm_package_dependencies_apollo_link_http":"^1.5.17","npm_package_dependencies__stripe_stripe_js":"^1.7.0","npm_package_scripts_lint":"eslint --ext .js,.vue --ignore-path .gitignore .","npm_config_registry":"https://registry.yarnpkg.com","CI_PROJECT_NAMESPACE_ID":"4176941","CI_PIPELINE_CREATED_AT":"2023-01-03T20:06:22Z","npm_package_dependencies__nuxtjs_apollo":"^4.0.1-rc.1","FF_USE_IMPROVED_URL_MASKING":"false","CI_CONCURRENT_PROJECT_ID":"0","CI_JOB_NAME_SLUG":"deploy-staging","npm_package_scripts_start":"nuxt start","npm_config_ignore_scripts":"","npm_config_version":"1.22.5","FF_KUBERNETES_HONOR_ENTRYPOINT":"false","CI_PIPELINE_URL":"https://gitlab.com/iq-talent-partners/iqtx/-/pipelines/737349904","npm_package_devDependencies_jest_transform_graphql":"^2.1.0","npm_package_dependencies_vue_notification":"^1.3.20","npm_package_dependencies_apollo_link":"^1.2.14","PATH":"/tmp/yarn--1672776509340-0.6672497635078982:/builds/iq-talent-partners/iqtx/node_modules/.bin:/usr/local/share/.config/yarn/link/node_modules/.bin:/usr/local/libexec/lib/node_modules/npm/bin/node-gyp-bin:/usr/local/lib/node_modules/npm/bin/node-gyp-bin:/usr/local/bin/node_modules/npm/bin/node-gyp-bin:/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin","NODE":"/usr/local/bin/node","CI_JOB_STARTED_AT":"2023-01-03T20:07:50Z","CI_SERVER_VERSION_MINOR":"8","CI_RUNNER_DESCRIPTION":"3-blue.shared.runners-manager.gitlab.com/default","npm_package_dependencies_vuex_persist":"^2.2.0","npm_package_dependencies_omit_deep_lodash":"^1.1.4","npm_package_name":"iqtx","FF_USE_NEW_BASH_EVAL_STRATEGY":"false","CI_PROJECT_TITLE":"iqtx","CI_PROJECT_VISIBILITY":"private","CI_COMMIT_TITLE":"Fix test","CI_BUILD_REF_SLUG":"develop","GITLAB_USER_EMAIL":"logan@serman.dev","npm_package_devDependencies_http_proxy":"^1.18.1","CI_SERVER":"yes","npm_package_devDependencies_cypress_capybara":"^0.0.2","FF_CMD_DISABLE_DELAYED_ERROR_LEVEL_EXPANSION":"false","CI_PROJECT_REPOSITORY_LANGUAGES":"vue,javascript,scss,shell","CI_PAGES_URL":"https://iq-talent-partners.gitlab.io/iqtx","CI_BUILD_BEFORE_SHA":"26064c7e06da24a592f50720e947ee92182ddeab","npm_package_devDependencies_eslint":"^6.8.0","npm_package_devDependencies__vue_test_utils":"^1.0.0-beta.27","npm_package_devDependencies__nuxtjs_eslint_config":"^2.0.2","CI_COMMIT_AUTHOR":"Logan Serman <logan@serman.dev>","SENTRY_PROJECT":"iqtx","npm_lifecycle_script":"nuxt build","npm_package_devDependencies__babel_runtime_corejs2":"^7.7.7","npm_package_dependencies_nuxt_responsive_loader":"^2.0.1","npm_package_lint_staged____js_vue_":"yarn lintfix","CI_JOB_IMAGE":"node:10-alpine","CI_RUNNER_SHORT_TOKEN":"zxwgkjAP","CI_PAGES_DOMAIN":"gitlab.io","npm_package_scripts_test":"jest","npm_config_version_git_message":"v%s","CI_REPOSITORY_URL":"https://gitlab-ci-token:64_zQ4Zjy4zgyW7oFDEsiTN@gitlab.com/iq-talent-partners/iqtx.git","CI_PROJECT_CLASSIFICATION_LABEL":"","CI_COMMIT_BRANCH":"develop","npm_lifecycle_event":"build","npm_package_devDependencies_sass_variable_loader":"^0.1.2","npm_package_dependencies_vuex_router_sync":"^5.0.0","npm_package_dependencies__nuxtjs_proxy":"^1.3.3","npm_package_husky_hooks_pre_commit":"lint-staged","npm_package_version":"1.0.0","FF_USE_POWERSHELL_PATH_RESOLVER":"false","CI_RUNNER_ID":"12270835","CI_ENVIRONMENT_TIER":"staging","npm_config_argv":"{\"remain\":[],\"cooked\":[\"run\",\"build\"],\"original\":[\"build\"]}","npm_package_devDependencies_nuxt_webfontloader":"^1.1.0","npm_package_devDependencies_lint_staged":"^10.1.1","npm_package_dependencies_graphql":"^15.0.0","npm_package_scripts_build":"nuxt build","DOCKER_TLS_CERTDIR":"","CI_JOB_JWT_V1":"eyJhbGciOiJSUzI1NiIsImtpZCI6IjRpM3NGRTdzeHFOUE9UN0ZkdmNHQTFaVkdHSV9yLXRzRFhuRXVZVDRacUUiLCJ0eXAiOiJKV1QifQ.eyJuYW1lc3BhY2VfaWQiOiI0MTc2OTQxIiwibmFtZXNwYWNlX3BhdGgiOiJpcS10YWxlbnQtcGFydG5lcnMiLCJwcm9qZWN0X2lkIjoiMTYxOTk3OTYiLCJwcm9qZWN0X3BhdGgiOiJpcS10YWxlbnQtcGFydG5lcnMvaXF0eCIsInVzZXJfaWQiOiIyODY3NjMiLCJ1c2VyX2xvZ2luIjoibG9nYW5zZXJtYW4iLCJ1c2VyX2VtYWlsIjoibG9nYW5Ac2VybWFuLmRldiIsInBpcGVsaW5lX2lkIjoiNzM3MzQ5OTA0IiwicGlwZWxpbmVfc291cmNlIjoicHVzaCIsImpvYl9pZCI6IjM1NDkzNTIyMTkiLCJyZWYiOiJkZXZlbG9wIiwicmVmX3R5cGUiOiJicmFuY2giLCJyZWZfcHJvdGVjdGVkIjoidHJ1ZSIsImVudmlyb25tZW50Ijoic3RhZ2luZyIsImVudmlyb25tZW50X3Byb3RlY3RlZCI6ImZhbHNlIiwiZGVwbG95bWVudF90aWVyIjoic3RhZ2luZyIsImp0aSI6ImI5NDZlNjEyLWQ3MDUtNGIzOC05ZDBhLTU4YTZjMGE4NDg1OCIsImlzcyI6ImdpdGxhYi5jb20iLCJpYXQiOjE2NzI3NzY0NzAsIm5iZiI6MTY3Mjc3NjQ2NSwiZXhwIjoxNjcyNzgwMDcwLCJzdWIiOiJqb2JfMzU0OTM1MjIxOSJ9.HlN9z6ynr3WSDu3GcDTr6UCYqh7Q1oFHzk7kfjAs-jNT2nJRj5FwcBKtx703fmEs6gO4ws34t8uhk8JjjyAG4pxS_y0CpeNlvfeqc9sCExula-i0BsmdJtO7Ah0XV1SI0VPBif7gNeKVHQAN9kO1q3hL18fnSPajiMdrteUaPS2EOmIZouEQ1GB3OryRvbg4SARpOMCpMleITU0YR59sUzppzDmjyMgH3JyEsVJZSNOeOTUu-qagACdBAeGIbJCYgwobgIvdPdv6lpIGSc_DX58BbqvUt7nt08UY7rsShrK6tsodBflQmz-ohScxarCUxgjW-kXHldQJX6K4I6cJcg","CI_REGISTRY":"registry.gitlab.com","CI_DEPENDENCY_PROXY_DIRECT_GROUP_IMAGE_PREFIX":"gitlab.com:443/iq-talent-partners/dependency_proxy/containers","CI_API_V4_URL":"https://gitlab.com/api/v4","GITLAB_USER_NAME":"Logan Serman","npm_package_devDependencies_cypress":"^4.12.1","CI_JOB_JWT_V2":"eyJhbGciOiJSUzI1NiIsImtpZCI6IjRpM3NGRTdzeHFOUE9UN0ZkdmNHQTFaVkdHSV9yLXRzRFhuRXVZVDRacUUiLCJ0eXAiOiJKV1QifQ.eyJuYW1lc3BhY2VfaWQiOiI0MTc2OTQxIiwibmFtZXNwYWNlX3BhdGgiOiJpcS10YWxlbnQtcGFydG5lcnMiLCJwcm9qZWN0X2lkIjoiMTYxOTk3OTYiLCJwcm9qZWN0X3BhdGgiOiJpcS10YWxlbnQtcGFydG5lcnMvaXF0eCIsInVzZXJfaWQiOiIyODY3NjMiLCJ1c2VyX2xvZ2luIjoibG9nYW5zZXJtYW4iLCJ1c2VyX2VtYWlsIjoibG9nYW5Ac2VybWFuLmRldiIsInBpcGVsaW5lX2lkIjoiNzM3MzQ5OTA0IiwicGlwZWxpbmVfc291cmNlIjoicHVzaCIsImpvYl9pZCI6IjM1NDkzNTIyMTkiLCJyZWYiOiJkZXZlbG9wIiwicmVmX3R5cGUiOiJicmFuY2giLCJyZWZfcHJvdGVjdGVkIjoidHJ1ZSIsImVudmlyb25tZW50Ijoic3RhZ2luZyIsImVudmlyb25tZW50X3Byb3RlY3RlZCI6ImZhbHNlIiwiZGVwbG95bWVudF90aWVyIjoic3RhZ2luZyIsImp0aSI6ImMwZmM0ZjUwLTU4Y2QtNGExOC04ZDJmLTQzMjc4NDIyOWMyNiIsImlzcyI6Imh0dHBzOi8vZ2l0bGFiLmNvbSIsImlhdCI6MTY3Mjc3NjQ3MCwibmJmIjoxNjcyNzc2NDY1LCJleHAiOjE2NzI3ODAwNzAsInN1YiI6InByb2plY3RfcGF0aDppcS10YWxlbnQtcGFydG5lcnMvaXF0eDpyZWZfdHlwZTpicmFuY2g6cmVmOmRldmVsb3AiLCJhdWQiOiJodHRwczovL2dpdGxhYi5jb20ifQ.FIY3cqTFSZvDqrmBPjp7WFU6yFsJNz2hog1HCzPhBCCEXmHNxcZ9hxA2gEpiAmPRGMs9f44UWku1plI-WmG4FDY5y6hga8lg7bqLNU7bz-PmPUQUq0WoQ43Ts4ERmw5XWBziN2MMaMBTxomAsBLaL28mzqTy7Q_50AFI9VZ9YuQetIUqD2GcePG2PMch3KifM8nQ8S_H3nlJ25Pb-XUT4SYACRTrLnEEHf-Rn6KmJIfRiVxg-Bmf8nHGsqDSAudWjo_bz6nZJ7gUEjCXtZ_Z7RyMYAkHPmtRHEaINfbaSX5YY2lTzbyLiwKJEYcPzhWgMlwGrDnJI1kc5L3tMhyYow","CI_PIPELINE_IID":"431","npm_config_version_git_tag":"true","npm_config_version_git_sign":"","CI_JOB_URL":"https://gitlab.com/iq-talent-partners/iqtx/-/jobs/3549352219","CI_BUILD_NAME":"deploy_staging","CI_RUNNER_EXECUTABLE_ARCH":"linux/amd64","npm_package_scripts_generate":"nuxt generate","npm_config_strict_ssl":"true","FIREBASE_TOKEN":"1//0fKQTNsK0dnQTCgYIARAAGA8SNwF-L9IrIU1NP0xeT7pBndu8gdhfnTyp5ghbQDSJ8Z1JMn49_Lq-xIatLL2icoLqZSSg579-awM","npm_package_devDependencies_vuetify":"^2.4.3","npm_package_devDependencies_eslint_plugin_prettier":"^3.1.2","npm_package_devDependencies_core_js":"2","npm_package_dependencies_firebase":"^7.16.1","npm_package_scripts_lintfix":"eslint --fix --ext .js,.vue --ignore-path .gitignore .","CI_JOB_JWT":"eyJhbGciOiJSUzI1NiIsImtpZCI6IjRpM3NGRTdzeHFOUE9UN0ZkdmNHQTFaVkdHSV9yLXRzRFhuRXVZVDRacUUiLCJ0eXAiOiJKV1QifQ.eyJuYW1lc3BhY2VfaWQiOiI0MTc2OTQxIiwibmFtZXNwYWNlX3BhdGgiOiJpcS10YWxlbnQtcGFydG5lcnMiLCJwcm9qZWN0X2lkIjoiMTYxOTk3OTYiLCJwcm9qZWN0X3BhdGgiOiJpcS10YWxlbnQtcGFydG5lcnMvaXF0eCIsInVzZXJfaWQiOiIyODY3NjMiLCJ1c2VyX2xvZ2luIjoibG9nYW5zZXJtYW4iLCJ1c2VyX2VtYWlsIjoibG9nYW5Ac2VybWFuLmRldiIsInBpcGVsaW5lX2lkIjoiNzM3MzQ5OTA0IiwicGlwZWxpbmVfc291cmNlIjoicHVzaCIsImpvYl9pZCI6IjM1NDkzNTIyMTkiLCJyZWYiOiJkZXZlbG9wIiwicmVmX3R5cGUiOiJicmFuY2giLCJyZWZfcHJvdGVjdGVkIjoidHJ1ZSIsImVudmlyb25tZW50Ijoic3RhZ2luZyIsImVudmlyb25tZW50X3Byb3RlY3RlZCI6ImZhbHNlIiwiZGVwbG95bWVudF90aWVyIjoic3RhZ2luZyIsImp0aSI6ImI5NDZlNjEyLWQ3MDUtNGIzOC05ZDBhLTU4YTZjMGE4NDg1OCIsImlzcyI6ImdpdGxhYi5jb20iLCJpYXQiOjE2NzI3NzY0NzAsIm5iZiI6MTY3Mjc3NjQ2NSwiZXhwIjoxNjcyNzgwMDcwLCJzdWIiOiJqb2JfMzU0OTM1MjIxOSJ9.HlN9z6ynr3WSDu3GcDTr6UCYqh7Q1oFHzk7kfjAs-jNT2nJRj5FwcBKtx703fmEs6gO4ws34t8uhk8JjjyAG4pxS_y0CpeNlvfeqc9sCExula-i0BsmdJtO7Ah0XV1SI0VPBif7gNeKVHQAN9kO1q3hL18fnSPajiMdrteUaPS2EOmIZouEQ1GB3OryRvbg4SARpOMCpMleITU0YR59sUzppzDmjyMgH3JyEsVJZSNOeOTUu-qagACdBAeGIbJCYgwobgIvdPdv6lpIGSc_DX58BbqvUt7nt08UY7rsShrK6tsodBflQmz-ohScxarCUxgjW-kXHldQJX6K4I6cJcg","CI_COMMIT_REF_SLUG":"develop","CI_DISPOSABLE_ENVIRONMENT":"true","PWD":"/builds/iq-talent-partners/iqtx","npm_execpath":"/opt/yarn-v1.22.5/bin/yarn.js","npm_package_dependencies_apollo_upload_client":"^13.0.0","FF_USE_NEW_SHELL_ESCAPE":"false","CI_RUNNER_TAGS":"[\"gce\", \"east-c\", \"linux\", \"ruby\", \"mysql\", \"postgres\", \"mongo\", \"git-annex\", \"shared\", \"docker\", \"saas-linux-small-amd64\"]","SENTRY_AUTO_ATTACH_COMMITS":"true","SENTRY_ORG":"iq-talent-partners","npm_package_dependencies_apollo_client":"^2.6.8","npm_package_author_name":"meatherly","CI_SERVER_TLS_CA_FILE":"/builds/iq-talent-partners/iqtx.tmp/CI_SERVER_TLS_CA_FILE","CI_PIPELINE_ID":"737349904","CI_PROJECT_PATH_SLUG":"iq-talent-partners-iqtx","CI_PROJECT_URL":"https://gitlab.com/iq-talent-partners/iqtx","CI_CONFIG_PATH":".gitlab-ci.yml","CI_COMMIT_BEFORE_SHA":"26064c7e06da24a592f50720e947ee92182ddeab","CI_BUILD_REF_NAME":"develop","CI_ENVIRONMENT_SLUG":"staging","npm_package_devDependencies_eslint_plugin_vue":"^6.1.2","npm_config_save_prefix":"^","npm_config_ignore_optional":"","CI_BUILD_STAGE":"deploy","CI_COMMIT_MESSAGE":"Fix test","npm_package_devDependencies_babel_eslint":"^10.0.3","npm_package_dependencies_debounce":"^1.2.0","npm_package_dependencies__nuxtjs_axios":"^5.3.6","npm_package_scripts_deploy":"firebase deploy --only hosting","FF_ENABLE_BASH_EXIT_CODE_CHECK":"false","CI_DEPENDENCY_PROXY_GROUP_IMAGE_PREFIX":"gitlab.com:443/iq-talent-partners/dependency_proxy/containers","GITLAB_USER_ID":"286763","INIT_CWD":"/builds/iq-talent-partners/iqtx","CI_JOB_STATUS":"running","CI_JOB_NAME":"deploy_staging","NODE_ENV":"production","_applied":"true"}
    }
    // Only set once
    if (!process.static && context.req) {
      app.context.req = context.req
    }
    if (!process.static && context.res) {
      app.context.res = context.res
    }
    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = formatUrl(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/replace
          window.location.replace(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext) {
  if (!promises.length || appContext._redirected || appContext._errored) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  let path = decodeURI(window.location.pathname)
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }
  // To get matched with sanitized router.base add trailing slash
  if (base && (path.endsWith('/') ? path : path + '/').startsWith(base)) {
    path = path.slice(base.length)
  }
  return (path || '/') + window.location.search + window.location.hash
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

/**
 * Format given url, append query to url query string
 *
 * @param  {string} url
 * @param  {string} query
 * @return {string}
 */
function formatUrl (url, query) {
  let protocol
  const index = url.indexOf('://')
  if (index !== -1) {
    protocol = url.substring(0, index)
    url = url.substring(index + 3)
  } else if (url.startsWith('//')) {
    url = url.substring(2)
  }

  let parts = url.split('/')
  let result = (protocol ? protocol + '://' : '//') + parts.shift()

  let path = parts.join('/')
  if (path === '' && parts.length === 1) {
    result += '/'
  }

  let hash
  parts = path.split('#')
  if (parts.length === 2) {
    [path, hash] = parts
  }

  result += path ? '/' + path : ''

  if (query && JSON.stringify(query) !== '{}') {
    result += (url.split('?').length === 2 ? '&' : '?') + formatQuery(query)
  }
  result += hash ? '#' + hash : ''

  return result
}

/**
 * Transform data object to query string
 *
 * @param  {object} query
 * @return {string}
 */
function formatQuery (query) {
  return Object.keys(query).sort().map((key) => {
    const val = query[key]
    if (val == null) {
      return ''
    }
    if (Array.isArray(val)) {
      return val.slice().map(val2 => [key, '=', val2].join('')).join('&')
    }
    return key + '=' + val
  }).filter(Boolean).join('&')
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = function urlJoin () {
  return [].slice
    .call(arguments)
    .join('/')
    .replace(/\/+/g, '/')
    .replace(':/', '://')
}
